import { v4 as uuid } from 'uuid';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import {
	DATA_REDACTED,
	ISSUE_CREATED,
	REDACTION_GROUPED_HISTORY_ITEM,
	type HistoryItem,
} from '../../types.tsx';

const HISTORY_GROUPED_ITEMS_CATEGORY = 'HISTORY' as const;
const TIME_BATCH = 5;

const isWithinTimeBatch = (item: HistoryItem, lastItem: HistoryItem) =>
	Math.abs(lastItem.timestamp.getTime() - item.timestamp.getTime()) / 60000 <= TIME_BATCH;

const isDescendingOrder = (firstItem: HistoryItem, lastItem: HistoryItem) =>
	firstItem.timestamp > lastItem.timestamp;

export const groupHistoryItems = (items: HistoryItem[]): HistoryItem[] => {
	if (!expVal('issue_view_activity_timeline', 'isActivityTimelineEnabled', false)) {
		return items;
	}

	if (!items.length) {
		return [];
	}
	const itemsMap = new Map();
	const orderIsDescending = isDescendingOrder(items[0], items[items.length - 1]);

	items.forEach((item: HistoryItem) => {
		if (item.type === ISSUE_CREATED) {
			// we don't want to group this entry, so setting the item's id (not actor's id) as a key to disable potential further grouping
			itemsMap.set(uuid(), item);
			return;
		}

		const actor = item.actor;

		// Group redacted items separately, follow same grouping logic
		if (item.type === DATA_REDACTED && fg('enable_redaction_support_in_jira_issue_view')) {
			// appending item type to differentiate redaction actors from other history items
			const currentItem = itemsMap.get(actor?.accountId + item.type);
			if (!currentItem) {
				if (actor?.accountId) {
					itemsMap.set(actor.accountId + DATA_REDACTED, item);
				}
			} else {
				const isAlreadyGrouped = currentItem.type === REDACTION_GROUPED_HISTORY_ITEM;
				if (isWithinTimeBatch(item, currentItem)) {
					if (isAlreadyGrouped) {
						currentItem.items.push(item);
					} else {
						itemsMap.set(actor?.accountId + DATA_REDACTED, {
							type: REDACTION_GROUPED_HISTORY_ITEM,
							timestamp: currentItem.timestamp,
							actor: currentItem.actor,
							items: [currentItem, item],
						});
					}
				} else {
					// don't need to be grouped, so setting the random id as a key to disable potential further grouping
					itemsMap.set(uuid(), currentItem);
					itemsMap.set(actor?.accountId + DATA_REDACTED, item);
				}
			}
			return;
		}

		const currentItem = itemsMap.get(actor?.accountId);
		if (!currentItem) {
			if (actor?.accountId) {
				itemsMap.set(actor.accountId, item);
			}
		} else {
			const isAlreadyGrouped = currentItem.type === HISTORY_GROUPED_ITEMS_CATEGORY;
			if (isWithinTimeBatch(item, currentItem)) {
				if (isAlreadyGrouped) {
					currentItem.items.push(item);
				} else {
					itemsMap.set(actor?.accountId, {
						type: HISTORY_GROUPED_ITEMS_CATEGORY,
						timestamp: currentItem.timestamp,
						actor: currentItem.actor,
						items: [currentItem, item],
					});
				}
			} else {
				// don't need to be grouped, so setting the random id as a key to disable potential further grouping
				itemsMap.set(uuid(), currentItem);
				itemsMap.set(actor?.accountId, item);
			}
		}
	});

	const sortedByTimeItems = Array.from(itemsMap.values()).sort((a, b) => {
		if (orderIsDescending) {
			return b.timestamp.getTime() - a.timestamp.getTime();
		}
		return a.timestamp.getTime() - b.timestamp.getTime();
	});

	return sortedByTimeItems;
};
